/**
 * Product Form Components
 *
 */
class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.cartElement = document.querySelector('cart-form');
    this.submitButton = this.querySelector('[type="submit"]');
    this.stickySubmitButton = this.querySelector('[data-stickybtn] [type="submit"]');
    this.qtyInput = this.querySelector('[data-qty-input]');

    // Bind once so add/remove use the same reference. Moving the element (e.g.
    // pdp-upsell shuffling slides between the live swiper and the pool with
    // appendChild) re-runs disconnected/connectedCallback; without a stable
    // reference each connect would stack another listener, causing duplicate
    // /cart/add.js submissions.
    this._onSubmit = this.onSubmitHandler.bind(this);
    this._listenerTargets = [];

    if (this.dataset.format === 'product-page' && typeof this.querySelector('variant-selects')?.selectCurrentVariantOnLoad === 'function') {
      this.querySelector('variant-selects')?.selectCurrentVariantOnLoad();
    }
  }

  connectedCallback() {
    this._removeBoundListeners();

    if (this.dataset.format === 'product-page') {
      // Listen at the form level so implicit submits (Enter-key in an input,
      // programmatic requestSubmit()) also route through the AJAX/cart-drawer
      // path. Button click listeners stay too: they cover the sticky button
      // that lives outside the <form>. _onSubmit calls evt.preventDefault(),
      // so the submit event firing first prevents any native double-submit.
      if (this.form) {
        this.form.addEventListener('submit', this._onSubmit);
        this._listenerTargets.push({ el: this.form, type: 'submit' });
      }

      const btns = this.form?.querySelectorAll('[type="submit"]') || [];
      btns.forEach(btn => {
        btn.addEventListener('click', this._onSubmit);
        this._listenerTargets.push({ el: btn, type: 'click' });
      });
    } else if (this.form) {
      this.form.addEventListener('submit', this._onSubmit);
      this._listenerTargets.push({ el: this.form, type: 'submit' });
    }
  }

  disconnectedCallback() {
    this._removeBoundListeners();
  }

  _removeBoundListeners() {
    if (!this._listenerTargets || !this._listenerTargets.length) return;
    this._listenerTargets.forEach(({ el, type }) => {
      el.removeEventListener(type, this._onSubmit);
    });
    this._listenerTargets = [];
  }

  /**
   * @param {object} [options]
   * @param {boolean} [options.deferUrl] - update the URL after the next paint (a shopper's change)
   */
  updateProductDetails(container, variant, { deferUrl = false } = {}) {
    if (!variant) {
      this.toggleAddButton('disable');
      return;
    }


    this.toggleAddButton('enable', variant.available);
    this.updateURLandID(variant, deferUrl);
    this.priceUpdate(container, variant);
    this.updateImage(container, variant.featured_media, variant);
  }

  /**
   * Render Price details based on current variant
   */
  priceUpdate(container, variant) {
    // Exclude descendants of nested product elements (e.g. <pdp-upsell>) so
    // changing the main product's variant does not overwrite prices inside
    // unrelated nested products that live inside [data-product-container].
    // Add other nested product custom elements here if they appear later.
    const NESTED_PRODUCT_EXCLUSION = ':not(pdp-upsell *)';

    // A variant is "on sale" only when compare_at_price is strictly greater
    // than price. This matches the Liquid template (snippets/product-price.liquid)
    // and avoids edge cases where compare_at_price is null/undefined/equal-or-less
    // than price (none of which should trigger sale styling).
    const isOnSale = variant.compare_at_price > variant.price;

    const priceElement = container.querySelectorAll(`[data-currentPrice]${NESTED_PRODUCT_EXCLUSION}`);
    if (!priceElement && priceElement.length <= 0) return;
    
    const comparePriceElement = container.querySelectorAll(`[data-comparePrice]${NESTED_PRODUCT_EXCLUSION}`);
    priceElement.forEach(e => {
      e.textContent = Utility.formatMoney(variant.price, window.globalSpace.money_format);
      e.classList.toggle('text-danger', isOnSale);
    })
    comparePriceElement.forEach(e => {
      if (e) {
        e.textContent = Utility.formatMoney(variant.compare_at_price, window.globalSpace.money_format);
        e.classList.toggle('d-none', !isOnSale);
      }
    })

    if(isOnSale){
      const savings = variant.compare_at_price - variant.price;
      const salePriceElem = container.querySelectorAll(`[data-saleprice]${NESTED_PRODUCT_EXCLUSION}`);
      salePriceElem.forEach(e => {
        if (e) {
           e.classList.remove('d-none');
          e.textContent = Utility.formatMoney(savings, window.globalSpace.money_format);
        }
      })
    }else{
      const salePriceElem = container.querySelectorAll(`[data-saleprice]${NESTED_PRODUCT_EXCLUSION}`);
      salePriceElem.forEach(e => {
        if (e) {
          e.classList.add('d-none');
        }
      });
    }

  }

  /**
   * Toggle the button based on product availability ( add to cart / soldOut )
   * @param {Text} status enable / disable
   * @param {Boolean} variantAvailable true/false
   */
  toggleAddButton(status, variantAvailable) {
    if (!this.submitButton) return;

    const addText = this.submitButton.querySelector('.add-text');
    if (status === 'disable' || (!variantAvailable)) {
        this.submitButton.setAttribute('disabled', true);
        if (addText) addText.textContent = status === 'disable' ? window.variantStrings.unavailable : window.variantStrings.soldOut;
    } else {
        this.submitButton.removeAttribute('disabled');
        if (addText) addText.textContent = this.form.classList.contains('cart-upsell-form') ? window.variantStrings.upsellAddText : window.variantStrings.addToCart;
    }

    if (!this.stickySubmitButton) return;

    const stickyAddText = this.stickySubmitButton.querySelector('.add-text');
    if (status === 'disable' || (!variantAvailable)) {
        this.stickySubmitButton.setAttribute('disabled', true);
        if (stickyAddText) stickyAddText.textContent = status === 'disable' ? window.variantStrings.unavailable : window.variantStrings.soldOut;
    } else {
        this.stickySubmitButton.removeAttribute('disabled');
        if (stickyAddText) stickyAddText.textContent = this.form.classList.contains('cart-upsell-form') ? window.variantStrings.upsellAddText : window.variantStrings.addToCart;
    }
  }

  /**
   * Update URL on variant change event
   * @param {JSON} currentVariant
   * @param {boolean} [deferUrl] - replace the URL after the next paint; the id input updates now
   */
  updateURLandID(currentVariant, deferUrl = false) {

    if (!currentVariant || !this.form) return;

    if (this.dataset.format === 'product-page') {
      const url = `${this.form.dataset.url}?variant=${currentVariant.id}`;
      // Third-party scripts wrap replaceState, so a shopper's change paints first (INP)
      const replaceUrl = () => window.history.replaceState({}, '', url);
      if (deferUrl) Utility.afterNextPaint(replaceUrl);
      else replaceUrl();
    }
    this.form.querySelector('input[name="id"]').value = currentVariant.id;
  }

  /**
   * Render selected variant image/slide
   * @param {element} container
   * @param {object} variantMedia
   */
  updateImage(container, variantMedia, currentVariant) {
    if (!variantMedia) {
      if (currentVariant && currentVariant.featured_image) {
        variantMedia = { preview_image: currentVariant.featured_image };
      } else {
        return;
      }
    }

    if (this.updateFeaturedImage(container, variantMedia)) {
      return;
    }
    
    // Try alternative image update method
    if (this.updateImageAlternative(container, variantMedia, currentVariant)) {
      return;
    }
  }

  updateFeaturedImage(container, variantMedia) {
    // Try both case variations of the data attribute
    let imgElem = container.querySelector('[data-feauredImage]');
    if (!imgElem) {
      imgElem = container.querySelector('[data-feauredimage]');
    }
    
    if (!imgElem) {
      // Try to find any image in the container
      const allImages = container.querySelectorAll('img');
      if (allImages.length > 0) {
        imgElem = allImages[0];
      }
    }
    
    if (!imgElem) {
      return false;
    }
    
    if (!variantMedia.preview_image?.src) {
      return false;
    }

    // Ensure the image URL has proper protocol
    let imageUrl = variantMedia.preview_image.src;
    if (imageUrl.startsWith('//')) {
      imageUrl = 'https:' + imageUrl;
    }
    
    // Remove lazy loading attributes and classes
    imgElem.removeAttribute('srcset');
    imgElem.removeAttribute('data-src');
    imgElem.removeAttribute('data-srcset');
    imgElem.classList.remove('lozad');
    
    // Update all image attributes to the new image
    imgElem.src = `${imageUrl}&width=850`;
    imgElem.setAttribute('data-src', `${imageUrl}&width=750`);
    
    // Create new srcset with proper protocol
    const newSrcset = `${imageUrl}&width=350 350w,${imageUrl}&width=550 550w,${imageUrl}&width=750 750w,${imageUrl}&width=991 991w,${imageUrl}&width=1200 1200w,${imageUrl}&width=1500 1500w,${imageUrl} 1540w`;
    imgElem.setAttribute('srcset', newSrcset);
    imgElem.setAttribute('data-srcset', newSrcset);
    
    return true;
  }

  /**
   * Alternative image update method for cases where updateFeaturedImage fails
   */
  updateImageAlternative(container, variantMedia, currentVariant) {
    // Try to get image from variant.featured_image if variantMedia doesn't work
    let imageSrc = null;
    if (variantMedia?.preview_image?.src) {
      imageSrc = variantMedia.preview_image.src;
    } else if (currentVariant?.featured_image?.src) {
      imageSrc = currentVariant.featured_image.src;
    }
    
    if (!imageSrc) {
      return false;
    }
    
    // Try multiple selectors to find the image
    const selectors = [
      '[data-feauredimage]',
      '[data-feauredImage]', 
      '[data-featured-image]',
      'img[data-feauredimage]',
      'img[data-feauredImage]',
      '.product-image img',
      '.product--media img',
      'img'
    ];
    
    let imgElem = null;
    for (const selector of selectors) {
      imgElem = container.querySelector(selector);
      if (imgElem) {
        break;
      }
    }
    
    if (!imgElem) {
      return false;
    }
    
    // Ensure the image URL has proper protocol
    let imageUrl = imageSrc;
    if (imageUrl.startsWith('//')) {
      imageUrl = 'https:' + imageUrl;
    }
    
    // Remove lazy loading attributes and classes
    imgElem.removeAttribute('srcset');
    imgElem.removeAttribute('data-src');
    imgElem.removeAttribute('data-srcset');
    imgElem.classList.remove('lozad');
    
    // Update all image attributes to the new image
    imgElem.src = `${imageUrl}&width=750`;
    imgElem.setAttribute('data-src', `${imageUrl}&width=750`);
    
    // Create new srcset with proper protocol
    const newSrcset = `${imageUrl}&width=350 350w,${imageUrl}&width=550 550w,${imageUrl}&width=750 750w,${imageUrl}&width=991 991w,${imageUrl}&width=1200 1200w,${imageUrl}&width=1500 1500w,${imageUrl} 1540w`;
    imgElem.setAttribute('srcset', newSrcset);
    imgElem.setAttribute('data-srcset', newSrcset);
    
    return true;
  }

  updateImageSlide(container, variantMedia) {
    const imageSlide = container.querySelector(`[data-productSlider] [data-mediaID="${variantMedia.id}"]`);
    if (!imageSlide) return;

    const slideIndex = Array.from(imageSlide.parentNode.children).indexOf(imageSlide);
    const slider = container.querySelector('[data-productSlider]');
    if (slider?.swiper) {
      slider.swiper.slideTo(slideIndex, 0, false);
    }
  }

  // updateImageSlider(container, variantMedia, currentVariant){
  //   const imageSlides = container.querySelectorAll(`[data-productSlider] [data-mediaID]`);
    
  //   const handleizedOptions = currentVariant.options.map(this.handleize);

  //   imageSlides.forEach(element => {
  //     if(element.querySelector('img')){
  //       let src = element.querySelector('img').src;
  //       src = src.toLowerCase();
  //       const containsKeyword = handleizedOptions.some(keyword => src.includes(keyword));
  //       if(containsKeyword == false){
  //         element.classList.add('d-none')
  //       }else{
  //         element.classList.remove('d-none')
  //       }
  //     }
  //   });
  // }

 


  /**
   * Product Form Submit event
   *
   * @param {evt} Event instance
   */
  onSubmitHandler(evt) {
    evt.preventDefault();

    // Resolve the element to show loading state on. For form `submit` events
    // evt.submitter is the button that triggered the submission (Enter-key
    // submissions also populate it). Fall back to the click target when the
    // handler is invoked from a button click listener, then to the cached
    // submitButton for synthetic callers (e.g. component-variant-options.js).
    const submitter =
      evt.submitter ||
      (evt.currentTarget?.tagName === 'BUTTON' && evt.currentTarget?.type === 'submit'
        ? evt.currentTarget
        : null) ||
      this.submitButton;

    Utility.setLoadingState(true, submitter);

    const formData = this.prepareFormData();
    const fetchCfg = Utility.fetchConfig('javascript', { body: formData });
    
    fetch(routes.cart_add_url + '.js', {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: 'text/html',
      },
    })
    .then((response) => response.json())
    .then((res) => this.handleSubmitResponse(res))
    .catch((e) => Utility.handleError(e))
    .finally(() => {
      Utility.setLoadingState(false, submitter);
    });
  }

  prepareFormData() {
    const formData = new FormData(this.form);
    formData.append('sections', `${this.cartElement?.dataset?.sectionId || 'template-cart'}`);
    return formData;
  }

  handleSubmitResponse(res) {
    const dataJSON = ((typeof res == 'object') ? res : JSON.parse(res));

    if (dataJSON.status === 422) {
      Utility.handleError(this.cleanErrorMessage(dataJSON.message), 'Add to Cart');
      return;
    } else if (dataJSON.errors) {
      Utility.handleError(dataJSON.errors, 'Add to Cart');
      return;
    }

    this.handleSubmitSuccess(dataJSON);
  }

  cleanErrorMessage(message) {
    // Remove "Validation failed:" prefix if present
    let msg = message.replace(/^Validation failed:\s*/i, "");

    // Fix duplicates like "Send on Send on ..." → "Send on ..."
    msg = msg.replace(/\b([A-Za-z ]+)\s+\1\b/gi, "$1");

    return msg.trim();
  }

  handleSubmitSuccess(dataJSON) {
    eventBus.publish(PUB_SUB_EVENTS.productATC, {
      data: {
        item: dataJSON,
        sections: dataJSON.sections
      },
    });

    if (this.qtyInput) {
      this.qtyInput.value = this.qtyInput.min || 1;
    }
    
    if (this.cartElement) {
      this.cartElement?._updateCart(dataJSON.sections, this.submitButton);
    } else {
      this.updateCartWithoutElement(dataJSON);
    }
  }

  updateCartWithoutElement(dataJSON) {
    const cartSectionID = this.cartElement?.dataset?.sectionId || 'template-cart';
    let cartHTML = dataJSON.sections[cartSectionID];
    cartHTML = new DOMParser().parseFromString(cartHTML, 'text/html');

    const cartJSONEle = cartHTML.querySelector('[data-cartScriptJSON]');
    window.globalSpace.cart = JSON.parse(cartJSONEle.textContent);

    updateCartCountInHeader();
    Utility.handleSuccess(dataJSON.title, 'Added to Cart');
  }
}

customElements.define('product-form', ProductForm);
